import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { CreationCard } from "@/components/CreationCard";
import { TOOLS, CORE_TOOLS } from "@/config/tools";
import { SERVICE_BADGES } from "@/config/badges";
import client, { mediaUrl } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Zap, Plus, ArrowRight, Sparkles } from "lucide-react";

export default function Dashboard() {
  const { user, wallet } = useAuth();
  const navigate = useNavigate();
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get("/generations?limit=8&status=completed").then(({ data }) => setRecent(data.items)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-5 py-12 sm:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="eyebrow text-primary">Welcome back</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight">Hi, {user?.name?.split(" ")[0] || "Creator"} 👋</h1>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4">
          <div>
            <p className="text-xs text-muted-foreground">Credit balance</p>
            <p className="flex items-center gap-1.5 text-2xl font-bold"><Zap className="h-5 w-5 text-primary" fill="currentColor" />{wallet.available_balance}</p>
          </div>
          <Button data-testid="buy-credits-button" onClick={() => navigate("/billing")} className="rounded-full"><Plus className="mr-1 h-4 w-4" /> Buy Credits</Button>
        </div>
      </div>

      <div className="mt-12">
        <h2 className="mb-6 text-2xl font-bold tracking-tight">What would you like to create?</h2>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {CORE_TOOLS.map((s, i) => (
            <CreationCard key={s} service={s} name={TOOLS[s].title} description={TOOLS[s].subtitle} icon={TOOLS[s].icon} badge={SERVICE_BADGES[s]} index={i} />
          ))}
        </div>
        <Button variant="ghost" className="mt-4 rounded-full" onClick={() => navigate("/create#tools")}>View all tools <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
      </div>

      <div className="mt-14">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold tracking-tight">Recent creations</h2>
          {recent.length > 0 && <Button variant="link" onClick={() => navigate("/library")}>View all</Button>}
        </div>
        {loading ? (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">{[...Array(4)].map((_, i) => <div key={i} className="shimmer aspect-square rounded-xl" />)}</div>
        ) : recent.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border py-16 text-center">
            <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-primary/10 text-primary"><Sparkles className="h-7 w-7" /></span>
            <p className="mt-4 font-semibold">Your creative library is empty.</p>
            <p className="text-sm text-muted-foreground">Let's create something amazing.</p>
            <Button className="mt-5 rounded-full" onClick={() => navigate("/create")}>Start Creating</Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {recent.map((g) => (
              <button key={g.id} onClick={() => navigate("/library")} className="group relative overflow-hidden rounded-xl border border-border bg-secondary/40">
                {g.output_urls?.[0] ? (
                  g.media_type === "video" ? (
                    <video
                      src={`${mediaUrl(g.output_urls[0])}#t=0.1`}
                      muted
                      playsInline
                      preload="metadata"
                      className="aspect-square w-full object-cover transition-transform group-hover:scale-105"
                    />
                  ) : (
                    <img src={mediaUrl(g.output_urls[0])} alt={g.service} className="aspect-square w-full object-cover transition-transform group-hover:scale-105" />
                  )
                ) : (
                  <div className="grid aspect-square place-items-center text-xs text-muted-foreground">{g.status}</div>
                )}
                <span className="absolute bottom-2 left-2 rounded-full bg-background/90 px-2 py-0.5 text-[10px] font-semibold capitalize">{TOOLS[g.service]?.title || g.service}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
